import { useEffect, useState } from 'react';
import * as criminalAppellateService from '../../../services/criminalAppellateCaseRequests';
import type { LookupItem } from '../../../mocks/criminalMasterData';

export type CriminalAppellateLookupScope = 'step1' | 'step3' | 'step4' | 'review';

type LookupBundle = {
  provinces: LookupItem[];
  districts: LookupItem[];
  policeStations: LookupItem[];
  urgencyLevels: LookupItem[];
  deliveryMethods: LookupItem[];
  corporateInstitutes: LookupItem[];
};

const EMPTY: LookupBundle = {
  provinces: [],
  districts: [],
  policeStations: [],
  urgencyLevels: [],
  deliveryMethods: [],
  corporateInstitutes: [],
};

let sharedLookups: LookupBundle = { ...EMPTY };

const scopeFetchers: Record<CriminalAppellateLookupScope, () => Promise<Partial<LookupBundle>>> = {
  step1: async () => {
    const [prov, corp] = await Promise.all([
      criminalAppellateService.getProvinces(),
      criminalAppellateService.getCorporateInstitutes(),
    ]);
    return {
      provinces: prov.success ? prov.data : [],
      corporateInstitutes: corp.success ? corp.data : [],
    };
  },
  step3: async () => {
    const urg = await criminalAppellateService.getUrgencyLevels();
    return { urgencyLevels: urg.success ? urg.data : [] };
  },
  step4: async () => {
    const del = await criminalAppellateService.getDeliveryMethods();
    return { deliveryMethods: del.success ? del.data : [] };
  },
  review: async () => {
    const [prov, urg, del, corp] = await Promise.all([
      criminalAppellateService.getProvinces(),
      criminalAppellateService.getUrgencyLevels(),
      criminalAppellateService.getDeliveryMethods(),
      criminalAppellateService.getCorporateInstitutes(),
    ]);
    return {
      provinces: prov.success ? prov.data : [],
      urgencyLevels: urg.success ? urg.data : [],
      deliveryMethods: del.success ? del.data : [],
      corporateInstitutes: corp.success ? corp.data : [],
    };
  },
};

/**
 * Step-scoped criminal appellate master data. Service-layer cache prevents duplicate network calls.
 */
export function useCriminalAppellateLookups(
  scope: CriminalAppellateLookupScope,
  provinceId?: string,
  districtId?: string,
) {
  const [data, setData] = useState<LookupBundle>(sharedLookups);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    void scopeFetchers[scope]().then((partial) => {
      if (!mounted) return;
      sharedLookups = { ...sharedLookups, ...partial };
      setData({ ...sharedLookups });
      setLoading(false);
    });

    return () => {
      mounted = false;
    };
  }, [scope]);

  useEffect(() => {
    if (scope !== 'step1' && scope !== 'review') return;
    if (!provinceId) {
      setData((d) => ({ ...d, districts: [] }));
      return;
    }

    let mounted = true;
    void criminalAppellateService.getDistricts(provinceId).then((r) => {
      if (!mounted) return;
      const districts = r.success ? r.data : [];
      sharedLookups = { ...sharedLookups, districts };
      setData((d) => ({ ...d, districts }));
    });
    return () => {
      mounted = false;
    };
  }, [scope, provinceId]);

  useEffect(() => {
    if (scope !== 'step1' && scope !== 'review') return;
    if (!districtId) {
      setData((d) => ({ ...d, policeStations: [] }));
      return;
    }

    let mounted = true;
    void criminalAppellateService.getPoliceStations(districtId).then((police) => {
      if (!mounted) return;
      const policeStations = police.success ? police.data : [];
      sharedLookups = { ...sharedLookups, policeStations };
      setData((d) => ({ ...d, policeStations }));
    });

    return () => {
      mounted = false;
    };
  }, [scope, districtId]);

  return {
    loading,
    provinces: data.provinces,
    districts: data.districts,
    policeStations: data.policeStations,
    urgencyLevels: data.urgencyLevels,
    deliveryMethods: data.deliveryMethods,
    corporateInstitutes: data.corporateInstitutes,
  };
}
